import { useRouteError } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Logo } from '@/components/brand/Logo';

/*
 * RouteErrorPage (BUG-H1, 30.10.2026).
 *
 * React Routerin errorElement: korvaa kirjaston oman "Unexpected Application Error!" -ruudun,
 * jonka demoasiakas näki Tuota-moduulissa. Juurisyy oli selaimen automaattinen käännös:
 * Chrome korvaa sivun tekstisolmut <font>-elementeillä Reactin ohi, ja seuraava ehdollinen
 * renderöinti (esim. Uusi postaus -ikkunan kanavan valintamerkki tekstin edessä) kaatuu
 * insertBefore/removeChild-kutsuun NotFoundErrorilla. Sama ruutu kattaa kaikki
 * renderöintivirheet, joita React Router saa kiinni.
 *
 * - Moduulireiteillä (ilman fullPage) sivu renderöityy sovelluskuoren sisälle: sivupalkki ja
 *   yläpalkki pysyvät, joten käyttäjä pääsee muualle ilman uudelleenlatausta.
 * - fullPage: juuritaso (kuori tai julkinen sivu kaatui) -> koko sivu logolla.
 * - DOM-ristiriita tunnistetaan virheviestistä -> vihje käännöksestä ja laajennuksista.
 */
const DOM_CONFLICT = /insertBefore|removeChild|NotFoundError|not a child of this node/i;

function describeError(error: unknown): { message: string; domConflict: boolean } {
  const message =
    error instanceof Error ? `${error.name}: ${error.message}` : String(error ?? '');
  return { message, domConflict: DOM_CONFLICT.test(message) };
}

type RouteErrorPageProps = {
  /** Juuritaso: ei sovelluskuorta ympärillä -> koko sivu logolla. */
  fullPage?: boolean;
};

export function RouteErrorPage({ fullPage = false }: RouteErrorPageProps) {
  const { t } = useTranslation();
  const error = useRouteError();
  const { message, domConflict } = describeError(error);

  const content = (
    <div
      className="mx-auto w-full max-w-lg space-y-4 rounded-md border border-kaste bg-white p-6 shadow-card"
      role="alert"
      data-testid="route-error"
    >
      <h1 className="text-xl font-semibold text-metsa">{t('routeError.title')}</h1>
      <p className="text-base text-teksti">{t('routeError.body')}</p>
      {domConflict && (
        <p
          className="rounded-sm bg-koivu/60 p-3 text-sm text-teksti"
          data-testid="route-error-translate-hint"
        >
          {t('routeError.translateHint')}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={() => window.location.reload()} data-testid="route-error-reload">
          {t('routeError.reload')}
        </Button>
        <a
          href="/"
          className="inline-flex h-9 items-center justify-center rounded-sm border border-sammal px-4 text-base font-medium text-sammal transition-colors hover:bg-ruoho hover:text-metsa focus-ring"
          data-testid="route-error-home"
        >
          {t('routeError.home')}
        </a>
      </div>
      {message && (
        <details className="text-xs text-usva-t">
          <summary className="cursor-pointer">{t('routeError.details')}</summary>
          <pre className="mt-2 whitespace-pre-wrap break-words">{message}</pre>
        </details>
      )}
    </div>
  );

  if (!fullPage) {
    return <div className="p-4 sm:p-6">{content}</div>;
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-usva p-6">
      <Logo className="mb-6 text-3xl" />
      {content}
    </div>
  );
}
